/**
 * React Flow ↔ StoryGraph adapter utilities.
 *
 * Converts between our persisted StoryGraph format and the
 * node/edge types expected by @xyflow/react.
 */

import type { Node, Edge } from '@xyflow/react';
import type {
  StoryGraph,
  StoryGraphNode,
  StoryGraphEdge,
} from '../../../Shared/src/@types';

// =============================================================================
// TO REACT FLOW
// =============================================================================

export function toReactFlowNodes(
  graph: StoryGraph,
  openSceneTab?: (sceneId: number, name: string) => void
): Node[] {
  return graph.nodes.map((n) => ({
    id: n.id,
    type: 'sceneNode',
    position: n.position,
    data: {
      nodeId: n.id,
      storySceneId: n.storySceneId,
      isStart: n.id === graph.startNodeId,
      openSceneTab,
    },
  }));
}

export function toReactFlowEdges(graph: StoryGraph, danglingEdgeIds?: Set<string>): Edge[] {
  return graph.edges.map((e) => {
    const dangling = danglingEdgeIds?.has(e.id) ?? false;
    return {
      id: e.id,
      source: e.from,
      target: e.to,
      type: 'smoothstep',
      animated: !dangling,
      label: e.label || e.condition || undefined,
      data: { condition: e.condition, dangling },
      className: dangling ? 'sm-edge-dangling' : undefined,
      style: dangling
        ? { stroke: '#ff6b6b', strokeDasharray: '6 3' }
        : { stroke: '#b87333' },
    };
  });
}

// =============================================================================
// FROM REACT FLOW
// =============================================================================

export function fromReactFlowNodes(
  nodes: Node[],
  startNodeId: string
): StoryGraphNode[] {
  return nodes.map((n) => ({
    id: n.id,
    storySceneId: n.data?.storySceneId as number,
    position: { x: n.position.x, y: n.position.y },
    type: 'scene' as const,
  }));
}

export function fromReactFlowEdges(edges: Edge[]): StoryGraphEdge[] {
  return edges.map((e) => ({
    id: e.id,
    from: e.source,
    to: e.target,
    // Prefer data.condition (new format); fall back to label (legacy format)
    condition: (e.data?.condition as string) || (e.label as string) || undefined,
    label: (e.label as string) || undefined,
  }));
}

/**
 * Reconstruct a full StoryGraph from current React Flow state.
 */
export function graphFromReactFlow(
  nodes: Node[],
  edges: Edge[],
  startNodeId: string
): StoryGraph {
  return {
    nodes: fromReactFlowNodes(nodes, startNodeId),
    edges: fromReactFlowEdges(edges),
    startNodeId,
  };
}
